import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Lock, GripVertical, ArrowRight, Upload, Download, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STAGE_COLORS = [
  "hsl(240 3% 55%)",   // gray
  "hsl(222 100% 33%)", // IKB blue
  "hsl(32 94% 44%)",   // amber
  "hsl(280 55% 45%)",  // violet
  "hsl(160 84% 30%)",  // emerald
  "hsl(2 78% 56%)",    // red
  "hsl(190 90% 40%)",  // cyan
  "hsl(340 82% 52%)",  // pink
];

export default function Settings() {
  const [tab, setTab] = useState("stages");
  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="settings-page">
      <div>
        <div className="eyebrow mb-2">Admin</div>
        <h1 className="text-3xl tracking-tight font-medium">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure pipeline stages, activity statuses, and bulk-import deals.</p>
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        {[
          { k: "stages",   label: "Pipeline stages" },
          { k: "statuses", label: "Activity statuses" },
          { k: "import",   label: "Bulk import" },
        ].map(({ k, label }) => (
          <button
            key={k}
            data-testid={`settings-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors duration-150 ${
              tab === k
                ? "border-primary text-primary font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "stages"   && <StageManager />}
      {tab === "statuses" && <StatusManager />}
      {tab === "import"   && <BulkImport />}
    </div>
  );
}

/* --------------- STAGES --------------- */
function StageManager() {
  const qc = useQueryClient();
  const { data: stages = [] } = useQuery({ queryKey: ["stages"], queryFn: async () => (await api.get("/settings/stages")).data });

  const [editing, setEditing] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null); // stage to delete
  const [moveTo, setMoveTo] = useState("");
  const [impactedCount, setImpactedCount] = useState(0);

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["stages"] });
    qc.invalidateQueries({ queryKey: ["deals"] });
    qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
  };

  const createM = useMutation({
    mutationFn: async (p) => (await api.post("/settings/stages", p)).data,
    onSuccess: () => { invalidateAll(); toast.success("Stage added"); setAddOpen(false); },
    onError:   (e) => toast.error(formatApiError(e)),
  });
  const updateM = useMutation({
    mutationFn: async ({ id, payload }) => (await api.patch(`/settings/stages/${id}`, payload)).data,
    onSuccess: () => { invalidateAll(); toast.success("Stage updated"); setEditing(null); },
    onError:   (e) => toast.error(formatApiError(e)),
  });
  const deleteM = useMutation({
    mutationFn: async ({ id, move_to }) => (await api.delete(`/settings/stages/${id}`, { params: move_to ? { move_to } : {} })).data,
    onSuccess: () => { invalidateAll(); toast.success("Stage deleted"); setDelTarget(null); setMoveTo(""); setImpactedCount(0); },
    onError:   (e) => toast.error(formatApiError(e)),
  });

  const move = async (stage, dir) => {
    const sorted = [...stages].sort((a, b) => a.position - b.position);
    const idx = sorted.findIndex((s) => s.id === stage.id);
    const swapWith = sorted[idx + dir];
    if (!swapWith) return;
    await Promise.all([
      updateM.mutateAsync({ id: stage.id,    payload: { position: swapWith.position } }),
      updateM.mutateAsync({ id: swapWith.id, payload: { position: stage.position } }),
    ]);
  };

  const askDelete = async (s) => {
    // fetch impacted count by calling delete without move_to → server tells us how many
    try {
      await api.delete(`/settings/stages/${s.id}`);
      // if we get here, nothing was impacted, but we need to confirm anyway
      toast.success("Stage deleted");
      invalidateAll();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const m = /^(\d+)\s+deal/.exec(detail);
      if (m) {
        setDelTarget(s);
        setImpactedCount(parseInt(m[1], 10));
        setMoveTo("");
      } else if (e?.response?.status === 400) {
        toast.error(detail || "Cannot delete this stage");
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Drag arrows to reorder. Deleting a stage prompts you to move its deals.</p>
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-stage-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Plus className="h-4 w-4 mr-1.5" /> New stage
        </Button>
      </div>

      <ul className="card-flat divide-y divide-border">
        {stages.map((s, i) => (
          <li key={s.id} data-testid={`stage-row-${s.key}`} className="px-4 py-3 flex items-center gap-4">
            <div className="flex flex-col text-muted-foreground">
              <button
                onClick={() => move(s, -1)}
                disabled={i === 0}
                className="disabled:opacity-30 hover:text-primary"
                aria-label="Move up"
                data-testid={`stage-up-${s.key}`}
              >▲</button>
              <button
                onClick={() => move(s, 1)}
                disabled={i === stages.length - 1}
                className="disabled:opacity-30 hover:text-primary"
                aria-label="Move down"
                data-testid={`stage-down-${s.key}`}
              >▼</button>
            </div>
            <span className="stage-dot" style={{ background: s.color }} />
            <div className="flex-1">
              <div className="text-sm font-medium">{s.label}</div>
              <div className="text-[11px] text-muted-foreground mono">{s.key}{s.protected && " · built-in"}</div>
            </div>
            <button
              data-testid={`edit-stage-${s.key}`}
              onClick={() => setEditing(s)}
              className="text-muted-foreground hover:text-primary"
              aria-label="Edit stage"
            >
              <Pencil className="h-4 w-4" />
            </button>
            {s.protected ? (
              <Lock className="h-4 w-4 text-muted-foreground/60" />
            ) : (
              <button
                data-testid={`delete-stage-${s.key}`}
                onClick={() => askDelete(s)}
                className="text-muted-foreground hover:text-destructive"
                aria-label="Delete stage"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </li>
        ))}
      </ul>

      {/* Add stage dialog */}
      <StageDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSubmit={(payload) => createM.mutateAsync(payload)}
        title="Add pipeline stage"
        saving={createM.isPending}
      />

      {/* Edit stage dialog */}
      <StageDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        initial={editing}
        onSubmit={(payload) => updateM.mutateAsync({ id: editing.id, payload: { label: payload.label, color: payload.color } })}
        title="Edit pipeline stage"
        saving={updateM.isPending}
        editing
      />

      {/* Delete-with-reassign dialog */}
      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Move deals before deleting</DialogTitle></DialogHeader>
          {delTarget && (
            <div className="space-y-3">
              <p className="text-sm">
                <strong>{impactedCount}</strong> deal{impactedCount !== 1 ? "s" : ""} still live in{" "}
                <strong>{delTarget.label}</strong>. Pick a stage to move them to.
              </p>
              <div className="space-y-1.5">
                <Label>Move deals to</Label>
                <select
                  data-testid="delete-stage-target"
                  className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
                  value={moveTo}
                  onChange={(e) => setMoveTo(e.target.value)}
                >
                  <option value="">— Select stage —</option>
                  {stages.filter((s) => s.key !== delTarget.key).map((s) => (
                    <option key={s.id} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary/40 border border-border p-2 rounded">
                <ArrowRight className="h-3.5 w-3.5" />
                {impactedCount} deals will move to <strong>{stages.find((s) => s.key === moveTo)?.label || "…"}</strong>, then the stage is deleted.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button
              disabled={!moveTo || deleteM.isPending}
              onClick={() => deleteM.mutate({ id: delTarget.id, move_to: moveTo })}
              data-testid="confirm-delete-stage"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteM.isPending ? "Moving…" : "Move & delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StageDialog({ open, onOpenChange, onSubmit, initial, title, saving, editing = false }) {
  const [label, setLabel] = useState(initial?.label || "");
  const [key, setKey] = useState(initial?.key || "");
  const [color, setColor] = useState(initial?.color || STAGE_COLORS[0]);
  const [error, setError] = useState("");

  // Sync when initial changes (edit → different row)
  useState(() => { setLabel(initial?.label || ""); setKey(initial?.key || ""); setColor(initial?.color || STAGE_COLORS[0]); });

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setError(""); }}>
      <DialogContent onOpenAutoFocus={() => {
        setLabel(initial?.label || "");
        setKey(initial?.key || "");
        setColor(initial?.color || STAGE_COLORS[0]);
        setError("");
      }}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Label</Label>
            <Input data-testid="stage-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Discovery" />
          </div>
          {!editing && (
            <div className="space-y-1.5">
              <Label>Internal key</Label>
              <Input
                data-testid="stage-key"
                value={key}
                onChange={(e) => setKey(e.target.value.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, ""))}
                placeholder="e.g. discovery"
              />
              <div className="text-[11px] text-muted-foreground">Lowercase, no spaces. Can&rsquo;t be changed later.</div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {STAGE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`h-7 w-7 rounded-full border-2 transition-transform ${color === c ? "border-foreground scale-110" : "border-transparent"}`}
                  style={{ background: c }}
                  aria-label={`Choose color ${c}`}
                />
              ))}
            </div>
          </div>
          {error && <div className="text-xs text-destructive">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={saving || !label.trim() || (!editing && !key.trim())}
            data-testid="stage-save"
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={async () => {
              try {
                const payload = editing ? { label: label.trim(), color } : { label: label.trim(), key: key.trim(), color };
                await onSubmit(payload);
              } catch (e) {
                setError(formatApiError(e));
              }
            }}
          >
            {saving ? "Saving…" : editing ? "Save" : "Add stage"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------- STATUSES --------------- */
function StatusManager() {
  const qc = useQueryClient();
  const { data: statuses = [] } = useQuery({ queryKey: ["activity-statuses"], queryFn: async () => (await api.get("/settings/statuses")).data });

  const [editing, setEditing] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [moveTo, setMoveTo] = useState("");
  const [impactedCount, setImpactedCount] = useState(0);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["activity-statuses"] });
    qc.invalidateQueries({ queryKey: ["activities"] });
    qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
  };

  const createM = useMutation({
    mutationFn: async (p) => (await api.post("/settings/statuses", p)).data,
    onSuccess: () => { invalidate(); toast.success("Status added"); setAddOpen(false); },
    onError:   (e) => toast.error(formatApiError(e)),
  });
  const updateM = useMutation({
    mutationFn: async ({ id, payload }) => (await api.patch(`/settings/statuses/${id}`, payload)).data,
    onSuccess: () => { invalidate(); toast.success("Status updated"); setEditing(null); },
    onError:   (e) => toast.error(formatApiError(e)),
  });
  const deleteM = useMutation({
    mutationFn: async ({ id, move_to }) => (await api.delete(`/settings/statuses/${id}`, { params: move_to ? { move_to } : {} })).data,
    onSuccess: () => { invalidate(); toast.success("Status deleted"); setDelTarget(null); setMoveTo(""); setImpactedCount(0); },
    onError:   (e) => toast.error(formatApiError(e)),
  });

  const move = async (s, dir) => {
    const sorted = [...statuses].sort((a, b) => a.position - b.position);
    const idx = sorted.findIndex((x) => x.id === s.id);
    const swap = sorted[idx + dir];
    if (!swap) return;
    await Promise.all([
      updateM.mutateAsync({ id: s.id,    payload: { position: swap.position } }),
      updateM.mutateAsync({ id: swap.id, payload: { position: s.position } }),
    ]);
  };

  const askDelete = async (s) => {
    try {
      await api.delete(`/settings/statuses/${s.id}`);
      toast.success("Status deleted");
      invalidate();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const m = /^(\d+)\s+activity/.exec(detail);
      if (m) {
        setDelTarget(s);
        setImpactedCount(parseInt(m[1], 10));
        setMoveTo("");
      } else if (e?.response?.status === 400) {
        toast.error(detail || "Cannot delete this status");
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Built-in statuses (Planned, Completed, Cancelled) can be renamed but not deleted — dashboard KPIs depend on them.
        </p>
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-status-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Plus className="h-4 w-4 mr-1.5" /> New status
        </Button>
      </div>

      <ul className="card-flat divide-y divide-border">
        {statuses.map((s, i) => (
          <li key={s.id} data-testid={`status-row-${s.key}`} className="px-4 py-3 flex items-center gap-4">
            <div className="flex flex-col text-muted-foreground">
              <button onClick={() => move(s, -1)} disabled={i === 0} className="disabled:opacity-30 hover:text-primary">▲</button>
              <button onClick={() => move(s, 1)} disabled={i === statuses.length - 1} className="disabled:opacity-30 hover:text-primary">▼</button>
            </div>
            <span className="stage-dot" style={{ background: s.color }} />
            <div className="flex-1">
              <div className="text-sm font-medium">{s.label}</div>
              <div className="text-[11px] text-muted-foreground mono">{s.key}{s.protected && " · built-in"}</div>
            </div>
            <button
              data-testid={`edit-status-${s.key}`}
              onClick={() => setEditing(s)}
              className="text-muted-foreground hover:text-primary"
              aria-label="Edit status"
            >
              <Pencil className="h-4 w-4" />
            </button>
            {s.protected ? (
              <Lock className="h-4 w-4 text-muted-foreground/60" />
            ) : (
              <button
                data-testid={`delete-status-${s.key}`}
                onClick={() => askDelete(s)}
                className="text-muted-foreground hover:text-destructive"
                aria-label="Delete status"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </li>
        ))}
      </ul>

      <StageDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSubmit={(payload) => createM.mutateAsync(payload)}
        title="Add activity status"
        saving={createM.isPending}
      />
      <StageDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        initial={editing}
        onSubmit={(payload) => updateM.mutateAsync({ id: editing.id, payload: { label: payload.label, color: payload.color } })}
        title="Edit activity status"
        saving={updateM.isPending}
        editing
      />

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Move activities before deleting</DialogTitle></DialogHeader>
          {delTarget && (
            <div className="space-y-3">
              <p className="text-sm">
                <strong>{impactedCount}</strong> activity(ies) still use{" "}
                <strong>{delTarget.label}</strong>. Pick a status to move them to.
              </p>
              <div className="space-y-1.5">
                <Label>Move activities to</Label>
                <select
                  data-testid="delete-status-target"
                  className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
                  value={moveTo}
                  onChange={(e) => setMoveTo(e.target.value)}
                >
                  <option value="">— Select status —</option>
                  {statuses.filter((s) => s.key !== delTarget.key).map((s) => (
                    <option key={s.id} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button
              disabled={!moveTo || deleteM.isPending}
              onClick={() => deleteM.mutate({ id: delTarget.id, move_to: moveTo })}
              data-testid="confirm-delete-status"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteM.isPending ? "Moving…" : "Move & delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* --------------- BULK IMPORT --------------- */
const CSV_TEMPLATE =
  "Deal Title,Company Name,Pipeline Stage,Due Date,Notes\n" +
  "Acme K12 Renewal,Acme Schools,New Lead,2026-06-30,Warm intro from partner\n" +
  ",Beta University,Qualified,,\n" +
  "Charlie Corp Expansion,Charlie Industries,,15/07/2026,\"Multi-year contract, decision by Q3\"\n";

function BulkImport() {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);

  const uploadM = useMutation({
    mutationFn: async (f) => {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/import/deals-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: (data) => {
      setResult(data);
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["stages"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      if (data.imported_deals > 0) {
        toast.success(`Imported ${data.imported_deals} deal${data.imported_deals > 1 ? "s" : ""}`);
      } else {
        toast.warning("No deals imported. Check errors below.");
      }
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "elevante-deals-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="bulk-import-panel">
      <div className="rounded-lg border border-border bg-card p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center">
            <Upload className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-medium">Bulk import deals</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Upload a CSV to create deals in one shot. Companies and pipeline stages
              are auto-created if they don&apos;t already exist.
            </p>
          </div>
        </div>

        <div className="rounded-md border border-dashed border-border p-4 bg-muted/30 text-sm">
          <div className="font-medium mb-2 flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            Expected columns
          </div>
          <ul className="space-y-1 text-muted-foreground pl-6 list-disc">
            <li><span className="text-foreground font-medium">Company Name</span> — required (row is skipped if empty)</li>
            <li><span className="text-foreground font-medium">Deal Title</span> — optional (defaults to Company Name)</li>
            <li><span className="text-foreground font-medium">Pipeline Stage</span> — optional (defaults to <em>New Lead</em>; created if missing)</li>
            <li><span className="text-foreground font-medium">Due Date</span> — optional (YYYY-MM-DD, DD/MM/YYYY, etc.)</li>
            <li><span className="text-foreground font-medium">Notes</span> — optional (added as a deal note)</li>
          </ul>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            onClick={downloadTemplate}
            data-testid="download-csv-template"
          >
            <Download className="h-4 w-4 mr-2" />
            Download template
          </Button>

          <label
            htmlFor="csv-file-input"
            className="inline-flex items-center h-10 px-4 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground text-sm cursor-pointer transition-colors"
            data-testid="csv-file-label"
          >
            <FileText className="h-4 w-4 mr-2" />
            {file ? file.name : "Choose CSV file"}
          </label>
          <input
            id="csv-file-input"
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="csv-file-input"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setResult(null);
            }}
          />

          <Button
            onClick={() => file && uploadM.mutate(file)}
            disabled={!file || uploadM.isPending}
            data-testid="upload-csv-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploadM.isPending ? "Importing…" : "Import deals"}
          </Button>
        </div>
      </div>

      {result && (
        <div
          className="rounded-lg border border-border bg-card p-6 space-y-4"
          data-testid="import-result-panel"
        >
          <div className="flex items-center gap-2">
            {result.imported_deals > 0 ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            )}
            <h3 className="font-medium">Import summary</h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <StatBox label="Rows processed" value={result.total_rows} testid="stat-total-rows" />
            <StatBox label="Deals imported" value={result.imported_deals} testid="stat-imported-deals" accent="emerald" />
            <StatBox label="Companies added" value={result.created_companies} testid="stat-created-companies" />
            <StatBox label="Stages added" value={result.created_stages} testid="stat-created-stages" />
          </div>

          {result.errors?.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm space-y-2" data-testid="import-errors">
              <div className="font-medium text-amber-900 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {result.errors.length} row{result.errors.length > 1 ? "s" : ""} skipped
              </div>
              <ul className="pl-6 list-disc text-amber-900/90 space-y-0.5">
                {result.errors.slice(0, 20).map((e, i) => (
                  <li key={i}>Row {e.row}: {e.message}</li>
                ))}
                {result.errors.length > 20 && (
                  <li className="text-amber-900/70 italic">…and {result.errors.length - 20} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, testid, accent }) {
  return (
    <div className="rounded-md border border-border p-3" data-testid={testid}>
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-medium mt-1 ${accent === "emerald" ? "text-emerald-600" : "text-foreground"}`}>
        {value ?? 0}
      </div>
    </div>
  );
}
